import type { ReasoningUIPart, TextUIPart, UIMessageChunk } from 'ai';

import type { UIMessageWithMetadata } from './types';

export const DURABLE_PREVIEW_DATA_PART_TYPE = 'data-durable-preview';

export type DurablePreviewAppendEvent = {
  phase: 'chunks';
  source: 'model';
  workflowRunId: string;
  stepIndex: number;
  attemptId: string;
  seq: number;
  chunks: UIMessageChunk[];
};

export type DurablePreviewCommitEvent = {
  phase: 'commit';
  source: 'model';
  workflowRunId: string;
  stepIndex: number;
  attemptId: string;
};

export type DurablePreviewEvent = DurablePreviewAppendEvent | DurablePreviewCommitEvent;

export type DurablePreviewEntry = {
  key: string;
  workflowRunId: string;
  stepIndex: number;
  attemptId: string;
  message: UIMessageWithMetadata;
};

type DurablePreviewLane = DurablePreviewEntry & {
  activeParts: Map<string, number>;
  lastSeq: number;
};

export type DurablePreviewState = Map<string, DurablePreviewLane>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isUIMessageChunk(value: unknown): value is UIMessageChunk {
  return isRecord(value) && isNonEmptyString(value.type) && value.type !== DURABLE_PREVIEW_DATA_PART_TYPE;
}

/**
 * Parses the one transient data-part shape reserved for speculative durable output.
 * Nested chunks intentionally stay outside the canonical chat message state.
 */
export function parseDurablePreviewEvent(part: unknown): DurablePreviewEvent | null {
  if (!isRecord(part) || part.type !== DURABLE_PREVIEW_DATA_PART_TYPE || part.transient !== true) {
    return null;
  }

  const data = part.data;
  if (
    !isRecord(data) ||
    !isNonEmptyString(data.workflowRunId) ||
    !Number.isInteger(data.stepIndex) ||
    (data.stepIndex as number) < 0 ||
    !isNonEmptyString(data.attemptId)
  ) {
    return null;
  }

  if (data.source !== 'model') {
    return null;
  }

  const identity = {
    source: 'model' as const,
    workflowRunId: data.workflowRunId,
    stepIndex: data.stepIndex as number,
    attemptId: data.attemptId,
  };

  if (data.phase === 'commit') {
    return { phase: 'commit', ...identity };
  }

  if (
    data.phase !== 'chunks' ||
    !Number.isInteger(data.seq) ||
    (data.seq as number) < 0 ||
    !Array.isArray(data.chunks) ||
    !data.chunks.every(isUIMessageChunk)
  ) {
    return null;
  }

  return {
    phase: 'chunks',
    ...identity,
    seq: data.seq as number,
    chunks: data.chunks,
  };
}

export function getDurablePreviewKey({
  workflowRunId,
  stepIndex,
}: Pick<DurablePreviewEvent, 'workflowRunId' | 'stepIndex'>): string {
  return JSON.stringify([workflowRunId, stepIndex]);
}

function createDurablePreviewLane(key: string, event: DurablePreviewAppendEvent): DurablePreviewLane {
  return {
    key,
    workflowRunId: event.workflowRunId,
    stepIndex: event.stepIndex,
    attemptId: event.attemptId,
    lastSeq: -1,
    activeParts: new Map(),
    message: {
      id: `durable-preview:${event.workflowRunId}:${event.stepIndex}:${event.attemptId}`,
      role: 'assistant',
      parts: [],
      metadata: { files: [] },
    },
  };
}

type PreviewTextPart = TextUIPart | ReasoningUIPart;
type PreviewTextPartType = PreviewTextPart['type'];

function isPreviewTextPartType(value: string): value is PreviewTextPartType {
  return value === 'text' || value === 'reasoning';
}

function applyPreviewChunks(current: DurablePreviewLane, event: DurablePreviewAppendEvent): DurablePreviewLane | null {
  const next: DurablePreviewLane = {
    ...current,
    lastSeq: event.seq,
    activeParts: new Map(current.activeParts),
    message: {
      ...current.message,
      parts: current.message.parts.map((part) => ({ ...part })),
    },
  };

  for (const chunk of event.chunks) {
    if (chunk.type === 'start-step') {
      next.message.parts.push({ type: 'step-start' });
      continue;
    }
    if (chunk.type === 'finish-step') {
      next.activeParts.clear();
      continue;
    }

    const [partType, phase, ...suffix] = chunk.type.split('-');
    if (
      !isPreviewTextPartType(partType) ||
      (phase !== 'start' && phase !== 'delta' && phase !== 'end') ||
      suffix.length > 0
    ) {
      continue;
    }

    const previewChunk = chunk as {
      id?: unknown;
      delta?: unknown;
      providerMetadata?: PreviewTextPart['providerMetadata'];
    };
    if (typeof previewChunk.id !== 'string') {
      return null;
    }

    const activeKey = `${partType}:${previewChunk.id}`;
    if (phase === 'start') {
      if (next.activeParts.has(activeKey)) {
        return null;
      }

      next.message.parts.push({
        type: partType,
        text: '',
        state: 'streaming',
        providerMetadata: previewChunk.providerMetadata,
      } as PreviewTextPart);
      next.activeParts.set(activeKey, next.message.parts.length - 1);
      continue;
    }

    const partIndex = next.activeParts.get(activeKey);
    const part = partIndex === undefined ? undefined : next.message.parts[partIndex];
    if (partIndex === undefined || part?.type !== partType) {
      return null;
    }

    const previewPart = part as PreviewTextPart;
    if (phase === 'delta') {
      if (typeof previewChunk.delta !== 'string') {
        return null;
      }
      next.message.parts[partIndex] = {
        ...previewPart,
        text: previewPart.text + previewChunk.delta,
        providerMetadata: previewChunk.providerMetadata ?? previewPart.providerMetadata,
      };
      continue;
    }

    next.message.parts[partIndex] = {
      ...previewPart,
      state: 'done',
      providerMetadata: previewChunk.providerMetadata ?? previewPart.providerMetadata,
    };
    next.activeParts.delete(activeKey);
  }

  return next;
}

/**
 * Folds display-only model chunks without ever passing speculative output to useChat.
 * A new attempt replaces the prior lane; only the matching commit can clear it.
 */
export function reduceDurablePreviewState(
  current: DurablePreviewState,
  event: DurablePreviewEvent,
): DurablePreviewState {
  const key = getDurablePreviewKey(event);
  const currentLane = current.get(key);

  if (event.phase === 'commit') {
    if (currentLane?.attemptId !== event.attemptId) {
      return current;
    }

    const next = new Map(current);
    next.delete(key);
    return next;
  }

  if (currentLane?.attemptId === event.attemptId && event.seq <= currentLane.lastSeq) {
    return current;
  }

  const lane = currentLane?.attemptId === event.attemptId ? currentLane : createDurablePreviewLane(key, event);
  if (event.seq !== lane.lastSeq + 1) {
    if (!current.has(key)) {
      return current;
    }

    const next = new Map(current);
    next.delete(key);
    return next;
  }

  const updatedLane = applyPreviewChunks(lane, event);
  const next = new Map(current);
  if (updatedLane) {
    next.set(key, updatedLane);
  } else {
    next.delete(key);
  }
  return next;
}

export function getDurablePreviewEntries(state: DurablePreviewState): DurablePreviewEntry[] {
  return Array.from(state.values(), ({ key, workflowRunId, stepIndex, attemptId, message }) => ({
    key,
    workflowRunId,
    stepIndex,
    attemptId,
    message,
  }));
}

export function isDurablePreviewEventForChat(event: DurablePreviewEvent, chatUuid: string): boolean {
  const normalizedChatUuid = chatUuid.replace(/^new-chat-/, '');

  return normalizedChatUuid.length > 0 && event.workflowRunId.startsWith(`nova-chat/${normalizedChatUuid}/`);
}

/**
 * Speculative output is display-only. Tool, source, data, and file parts become actionable
 * only after the successful attempt is emitted through the canonical AI SDK stream.
 */
export function getRenderableDurablePreviewMessage(message: UIMessageWithMetadata): UIMessageWithMetadata | null {
  const parts = message.parts.filter(
    (part) => part.type === 'text' || part.type === 'reasoning' || part.type === 'step-start',
  );

  return parts.some((part) => part.type === 'text' || part.type === 'reasoning') ? { ...message, parts } : null;
}

export function hasVisibleDurablePreviewText(message: UIMessageWithMetadata): boolean {
  return message.parts.some((part) => part.type === 'text' && part.text.trim().length > 0);
}
